import React from 'react';
import { Button as AntButton, ButtonProps as AntButtonProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export type ButtonType = 'primary' | 'secondary' | 'link';

export interface ButtonProps extends Omit<AntButtonProps, 'type' | 'ghost'> {
  type?: ButtonType;
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => {
  const baseStyles = {
    fontWeight: theme.typography.fontWeight.medium,
    borderWidth: theme.border.width.sm,
    borderStyle: 'solid',
    borderRadius: theme.radius.sm,
    transition: 'all 0.2s ease-in-out',
    lineHeight: theme.typography.lineHeight.none,
    '&.ant-btn-round': {
      borderRadius: theme.radius.lg,
    },
    '&.ant-btn-circle': {
      borderRadius: theme.radius.full,
    },
  };

  return {
    // Primary button
    primaryButton: {
      ...baseStyles,
      backgroundColor: `${theme.color.semantic.brand.default.primary}`,
      color: `${theme.color.semantic.text.inverse}`,
      borderColor: `${theme.color.semantic.brand.default.primary}`,
      '&:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.secondary}`,
        color: `${theme.color.semantic.text.inverse}`,
        borderColor: `${theme.color.semantic.brand.default.secondary}`,
      },
      '&:active, &:focus': {
        backgroundColor: `${theme.color.semantic.brand.default.strong}`,
        color: `${theme.color.semantic.text.inverse}`,
        borderColor: `${theme.color.semantic.brand.default.strong}`,
      },
      '&:disabled, &.ant-btn-disabled, &:disabled:hover, &:disabled:active, &:disabled:focus, &.ant-btn-disabled:hover, &.ant-btn-disabled:active, &.ant-btn-disabled:focus': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft}`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft}`,
        borderColor: `${theme.color.semantic.border.subtle}`,
      },
      '&.ant-btn-loading, &.ant-btn-loading:hover, &.ant-btn-loading:active, &.ant-btn-loading:focus': {
        backgroundColor: `${theme.color.semantic.brand.default.primary}`,
        color: `${theme.color.semantic.text.inverse}`,
        borderColor: `${theme.color.semantic.brand.default.primary}`,
      },
    },

    // Primary danger button
    primaryDangerButton: {
      ...baseStyles,
      backgroundColor: theme.color.semantic.brand.danger.primary,
      color: theme.color.semantic.brand.danger.inverse,
      borderColor: theme.color.semantic.brand.danger.primary,
      '&:hover': {
        backgroundColor: theme.color.semantic.brand.danger.muted,
        color: theme.color.semantic.brand.danger.inverse,
        borderColor: theme.color.semantic.brand.danger.muted,
      },
      '&:active, &:focus': {
        backgroundColor: theme.color.semantic.brand.danger.primary,
        color: theme.color.semantic.brand.danger.inverse,
        borderColor: theme.color.semantic.brand.danger.primary,
      },
      '&:disabled, &.ant-btn-disabled, &:disabled:hover, &:disabled:active, &:disabled:focus, &.ant-btn-disabled:hover, &.ant-btn-disabled:active, &.ant-btn-disabled:focus': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft}`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft}`,
        borderColor: `${theme.color.semantic.border.soft}`,
      },
      '&.ant-btn-loading, &.ant-btn-loading:hover, &.ant-btn-loading:active, &.ant-btn-loading:focus': {
        backgroundColor: `${theme.color.semantic.brand.danger.primary}`,
        color: `${theme.color.semantic.brand.danger.inverse}`,
        borderColor: `${theme.color.semantic.brand.danger.primary}`,
      },
    },

    // Secondary button
    secondaryButton: {
      ...baseStyles,
      backgroundColor: `${theme.color.semantic.brand.default.subtle}`,
      color: `${theme.color.semantic.brand.default.secondary}`,
      borderColor: `${theme.color.semantic.brand.default.secondary}`,
      '&:hover': {
        backgroundColor: `${theme.color.semantic.brand.default.muted}`,
        color: `${theme.color.semantic.brand.default.secondary}`,
        borderColor: `${theme.color.semantic.brand.default.secondary}`,
      },
      '&:active, &:focus': {
        backgroundColor: `${theme.color.semantic.brand.default.light}`,
        color: `${theme.color.semantic.brand.default.primary}`,
        borderColor: `${theme.color.semantic.brand.default.primary}`,
      },
      '&:disabled, &.ant-btn-disabled, &:disabled:hover, &:disabled:active, &:disabled:focus, &.ant-btn-disabled:hover, &.ant-btn-disabled:active, &.ant-btn-disabled:focus': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft}`,
        opacity: `${theme.opacity[70]}`,
        color: `${theme.color.semantic.text.soft}`,
        borderColor: `${theme.color.semantic.text.soft}`,
      },
      '&.ant-btn-loading, &.ant-btn-loading:hover, &.ant-btn-loading:active, &.ant-btn-loading:focus': {
        backgroundColor: `${theme.color.semantic.brand.default.subtle}`,
        color: `${theme.color.semantic.brand.default.secondary}`,
        borderColor: `${theme.color.semantic.brand.default.secondary}`,
      },
    },

    // Secondary danger button
    secondaryDangerButton: {
      ...baseStyles,
      backgroundColor: `${theme.color.semantic.brand.danger.inverse}`,
      color: `${theme.color.semantic.brand.danger.primary}`,
      borderColor: `${theme.color.semantic.brand.danger.primary}`,
      '&:hover': {
        backgroundColor: `${theme.color.semantic.brand.danger.subtle}`,
        color: `${theme.color.semantic.brand.danger.primary}`,
        borderColor: `${theme.color.semantic.brand.danger.primary}`,
      },
      '&:active, &:focus': {
        backgroundColor: `${theme.color.semantic.brand.danger.light}`,
        color: `${theme.color.semantic.brand.danger.primary}`,
        borderColor: `${theme.color.semantic.brand.danger.primary}`,
      },
      '&:disabled, &.ant-btn-disabled, &:disabled:hover, &:disabled:active, &:disabled:focus, &.ant-btn-disabled:hover, &.ant-btn-disabled:active, &.ant-btn-disabled:focus': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft}`,
        opacity: `${theme.opacity[70]} !important`,
        color: `${theme.color.semantic.text.soft}`,
        borderColor: `${theme.color.semantic.border.soft}`,
      },
      '&.ant-btn-loading, &.ant-btn-loading:hover, &.ant-btn-loading:active, &.ant-btn-loading:focus': {
        backgroundColor: `${theme.color.semantic.brand.danger.inverse}`,
        color: `${theme.color.semantic.brand.danger.primary}`,
        borderColor: `${theme.color.semantic.brand.danger.primary}`,
      },
    },

    // Link button
    linkButton: {
      ...baseStyles,
      backgroundColor: 'transparent',
      color: `${theme.color.semantic.brand.default.primary}`,
      borderColor: 'transparent',
      boxShadow: 'none',
      padding: `${theme.spacing[0]} ${theme.spacing[1]}`,
      '&:hover': {
        backgroundColor: 'transparent',
        color: `${theme.color.semantic.brand.default.secondary}`,
        borderColor: 'transparent',
      },
      '&:active, &:focus': {
        backgroundColor: 'transparent',
        color: `${theme.color.semantic.brand.default.strong}`,
        borderColor: 'transparent',
      },
      '&:disabled, &.ant-btn-disabled, &:disabled:hover, &:disabled:active, &:disabled:focus, &.ant-btn-disabled:hover, &.ant-btn-disabled:active, &.ant-btn-disabled:focus': {
        backgroundColor: 'transparent',
        color: `${theme.color.semantic.text.soft}`,
        borderColor: 'transparent',
      },
      '&.ant-btn-loading, &.ant-btn-loading:hover, &.ant-btn-loading:active, &.ant-btn-loading:focus': {
        backgroundColor: 'transparent',
        color: `${theme.color.semantic.brand.default.primary}`,
        borderColor: 'transparent',
      },
    },

    // Link danger button
    linkDangerButton: {
      ...baseStyles,
      backgroundColor: 'transparent',
      color: `${theme.color.semantic.brand.danger.primary}`,
      borderColor: 'transparent',
      boxShadow: 'none',
      padding: `${theme.spacing[0]} ${theme.spacing[1]}`,
      '&:hover': {
        backgroundColor: 'transparent',
        color: `${theme.color.semantic.brand.danger.muted}`,
        borderColor: 'transparent',
      },
      '&:active, &:focus': {
        backgroundColor: 'transparent',
        color: `${theme.color.semantic.brand.danger.primary}`,
        borderColor: 'transparent',
      },
      '&:disabled, &.ant-btn-disabled, &:disabled:hover, &:disabled:active, &:disabled:focus, &.ant-btn-disabled:hover, &.ant-btn-disabled:active, &.ant-btn-disabled:focus': {
        backgroundColor: 'transparent',
        color: `${theme.color.semantic.text.soft}`,
        borderColor: 'transparent',
      },
      '&.ant-btn-loading, &.ant-btn-loading:hover, &.ant-btn-loading:active, &.ant-btn-loading:focus': {
        backgroundColor: 'transparent',
        color: `${theme.color.semantic.brand.danger.primary}`,
        borderColor: 'transparent',
      },
    },

    // Size variants
    smallButton: {
      height: '24px',
      fontSize: theme.typography.fontSize.sm,
      padding: `${theme.spacing[0]} ${theme.spacing[3]}`,
    },
    middleButton: {
      height: '32px',
      fontSize: theme.typography.fontSize.sm,
      padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
    },
    largeButton: {
      height: '40px',
      fontSize: theme.typography.fontSize.md,
      padding: `${theme.spacing[0]} ${theme.spacing[5]}`,
    },

    // Block button
    blockButton: {
      width: '100%',
    },

    // Icon-only button
    iconButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };
});

const ButtonInner = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'primary',
      size = 'middle',
      danger = false,
      block = false,
      shape = 'default',
      className = '',
      icon,
      children,
      ...rest
    },
    ref
  ) => {
    const classes = useStyles();

    const getSizeClass = () => {
      switch (size) {
        case 'small':
          return classes.smallButton;
        case 'large':
          return classes.largeButton;
        default:
          return classes.middleButton;
      }
    };

    const getButtonClass = () => {
      if (danger) {
        switch (type) {
          case 'primary':
            return classes.primaryDangerButton;
          case 'secondary':
            return classes.secondaryDangerButton;
          case 'link':
            return classes.linkDangerButton;
          default:
            return classes.primaryDangerButton;
        }
      }

      switch (type) {
        case 'primary':
          return classes.primaryButton;
        case 'secondary':
          return classes.secondaryButton;
        case 'link':
          return classes.linkButton;
        default:
          return classes.primaryButton;
      }
    };

    const isIconOnly = icon && !children;

    const combinedClassName = `${getSizeClass()} ${getButtonClass()} ${
      block ? classes.blockButton : ''
    } ${isIconOnly ? classes.iconButton : ''} ${className}`.trim();

    // Map our type to antd type for proper button rendering
    const antdType = type === 'link' ? 'link' : type === 'secondary' ? 'default' : 'primary';

    return (
      <AntButton
        ref={ref}
        type={antdType}
        size={size}
        danger={danger}
        block={block}
        shape={shape}
        icon={icon}
        {...rest}
        className={combinedClassName}
      >
        {children}
      </AntButton>
    );
  }
);

// Create Button with Group property
export const Button = Object.assign(ButtonInner, {
  Group: AntButton.Group,
});

export default Button;
